import { type ApiOrder, EnumProcessState } from '@Model';
import { useTranslate } from '@synqo/frontend-component-library';
import { useMemo, useState } from 'react';
import {
    type ContainerCorrection,
    containerCorrections,
    type ContainerCorrectionState,
    placeOf,
} from './containerCorrections';

/**
 * What saving changes about the booked containers, compared with the start of editing. Empty outside a
 * correction.
 */
// @ai-generated 2026-10-19T08:21:57+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function useContainerCorrections(
    order: ApiOrder,
    atEditStart: ContainerCorrectionState | undefined,
): ContainerCorrection[] {
    const i18n = useTranslate('');
    return useMemo(
        () =>
            atEditStart
                ? containerCorrections(
                      atEditStart,
                      { actions: order.container_actions, completedAt: order.completed_at, place: placeOf(order) },
                      i18n,
                  )
                : [],
        [atEditStart, order, i18n],
    );
}

/**
 * The containers, completion date and place at the start of a correction. Unset outside a correction, and when
 * the order only gets completed in this edit. The correction starts when the draft arrives with its own id.
 */
// @ai-generated 2026-10-19T10:54:39+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function useStateAtEditStart(order: ApiOrder, canEdit: boolean): ContainerCorrectionState | undefined {
    const edit = `${canEdit}|${order.id}`;
    const [snapshotOf, setSnapshotOf] = useState(edit);
    const [atEditStart, setAtEditStart] = useState<ContainerCorrectionState>({
        actions: order.container_actions,
        completedAt: order.completed_at,
        place: placeOf(order),
    });
    if (edit !== snapshotOf) {
        setSnapshotOf(edit);
        setAtEditStart({ actions: order.container_actions, completedAt: order.completed_at, place: placeOf(order) });
    }

    const corrects =
        canEdit && Boolean(atEditStart.completedAt) && order.process_state === EnumProcessState.ExecutionFinished;
    return corrects ? atEditStart : undefined;
}
